import moment from "moment";
import { Link } from "react-router-dom";
import { cn } from "../../../../utils/classnames";

const PAYMENT_STATUS_STYLES = {
  PAID: {
    style: "tw:bg-[#DFF9F7] tw:!text-[#3CAEA3]",
    text: "Paid",
  },
  CHARGE_PENDING: {
    style: "tw:bg-[#FFFAF0] tw:!text-[#F2AE09]",
    text: "Pending",
  },
  UNPAID: {
    style: "tw:bg-[#FDECEA] tw:!text-[#EA4335]",
    text: "Unpaid",
  },
};

const getPaymentStatus = (status) =>
  PAYMENT_STATUS_STYLES[status] || {
    style: "tw:bg-[#EA4335] tw:text-white",
    text: "-",
  };

const TABLE_COLUMNS = [
  { key: "no", title: "#", width: "tw:w-[60px] tw:rounded-tl-[5px]" },
  { key: "businessUser", title: "Business User", width: "tw:w-[280px]" },
  { key: "period", title: "Period", width: "tw:w-[160px]" },
  { key: "items", title: "Items", width: "tw:w-[100px]" },
  { key: "totalAmount", title: "Total Amount", width: "tw:w-[180px]" },
  { key: "payment", title: "Payment Status", width: "tw:w-[200px]" },
  { key: "updated", title: "Updated", width: "tw:w-[170px]" },
  { key: "action", title: "Action", width: "tw:w-[120px] tw:rounded-tr-[5px]", sticky: true },
];

const TABLE_COLUMN_COUNT = TABLE_COLUMNS.length;

const baseCellClass =
  "tw:h-[75px] tw:border-b tw:border-[#F3F3F3] tw:bg-white tw:px-[15px] tw:text-[15px] tw:leading-[22px] tw:align-middle";

const stickyCellClass =
  "tw:sticky tw:right-0 tw:z-10 tw:h-[75px] tw:border-b tw:border-x tw:border-[#F3F3F3] tw:bg-white tw:px-[15px] tw:text-center";

const Badge = ({ className, children }) => (
  <span
    className={cn(
      "tw:flex tw:w-full tw:h-[40px] tw:items-center tw:justify-center tw:whitespace-nowrap tw:rounded-[5px] tw:px-5 tw:text-[14px] tw:leading-[22px] tw:text-white",
      className,
    )}
  >
    {children}
  </span>
);

const HeaderCell = ({ title, width, sticky }) => (
  <th
    className={cn(
      "tw:h-[60px] tw:bg-[#282828] tw:px-[15px] tw:py-[20px] tw:text-left tw:text-[15px] tw:font-bold tw:leading-[22px] tw:text-white",
      width,
      sticky && "tw:sticky tw:right-0 tw:z-20",
    )}
  >
    <span className="tw:whitespace-nowrap">{title}</span>
  </th>
);

const MessageRow = ({ className, text }) => (
  <tr>
    <td colSpan={TABLE_COLUMN_COUNT} className={className}>
      {text}
    </td>
  </tr>
);

const SkeletonBar = ({ className }) => (
  <span className={cn("tw:block tw:h-[16px] tw:animate-pulse tw:rounded-[4px] tw:bg-[#EDEDED]", className)} />
);

const SkeletonCell = ({ column }) => {
  switch (column.key) {
    case "payment":
      return <SkeletonBar className="tw:h-[40px] tw:w-full tw:rounded-[5px]" />;
    case "businessUser":
      return (
        <div className="tw:flex tw:flex-col tw:gap-[8px]">
          <SkeletonBar className="tw:w-[70%]" />
          <SkeletonBar className="tw:h-[12px] tw:w-[45%]" />
        </div>
      );
    case "action":
      return <SkeletonBar className="tw:mx-auto tw:h-[30px] tw:w-[30px] tw:rounded-[2px]" />;
    case "no":
    case "items":
      return <SkeletonBar className="tw:w-[50%]" />;
    default:
      return <SkeletonBar className="tw:w-[80%]" />;
  }
};

const SkeletonRows = ({ rowCount }) =>
  Array.from({ length: rowCount }).map((_, rowIndex) => (
    <tr key={`skeleton-${rowIndex}`}>
      {TABLE_COLUMNS.map((column) => (
        <td key={column.key} className={column.sticky ? stickyCellClass : baseCellClass}>
          <SkeletonCell column={column} />
        </td>
      ))}
    </tr>
  ));

const InvoicesTable = ({ rows = [], startIndex = 0, loading = false, skeletonRowCount = 10 }) => (
  <section className="tw:mt-[34px] tw:w-full tw:overflow-x-auto">
    <table className="tw:w-full tw:min-w-[1200px] tw:border-separate tw:border-spacing-0">
      <thead>
        <tr>
          {TABLE_COLUMNS.map((column) => (
            <HeaderCell key={column.key} title={column.title} width={column.width} sticky={column.sticky} />
          ))}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <SkeletonRows rowCount={skeletonRowCount} />
        ) : rows.length === 0 ? (
          <MessageRow className={`${baseCellClass} tw:text-center tw:text-[#7C7B7B]`} text="No invoices found." />
        ) : (
          rows.map((invoice, index) => {
            const { _id, user, year, month, totalItem, totalAmount, paymentStatus, updatedDate } = invoice;
            const businessName =
              user?.businessDetail?.businessInformation?.businessName || user?.name || "-";
            const paymentMeta = getPaymentStatus(paymentStatus);

            return (
              <tr key={_id}>
                <td className={baseCellClass}>{startIndex + index + 1}</td>
                <td className={baseCellClass}>
                  <div className="tw:flex tw:flex-col">
                    <span className="tw:!text-[15px] tw:!text-[#282828] tw:leading-none tw:mb-1">
                      {businessName}
                    </span>
                    <span className="tw:!text-[13px] tw:!text-[#8D8D8D] tw:leading-none">{user?.email}</span>
                  </div>
                </td>
                <td className={`${baseCellClass} tw:whitespace-nowrap`}>
                  {moment(`${year}-${String(month).padStart(2, "0")}-01`).locale("en").format("MMMM YYYY")}
                </td>
                <td className={baseCellClass}>{totalItem}</td>
                <td className={`${baseCellClass} tw:whitespace-nowrap`}>
                  {(totalAmount || 0).toLocaleString()} JPY
                </td>
                <td className={baseCellClass}>
                  <Badge className={paymentMeta.style}>{paymentMeta.text}</Badge>
                </td>
                <td className={`${baseCellClass} tw:whitespace-nowrap`}>
                  {updatedDate ? moment(updatedDate).locale("en").format("YYYY/MM/DD HH:mm") : "-"}
                </td>
                <td className={stickyCellClass}>
                  <Link
                    to={`/admin/invoices/${_id}`}
                    className="tw:border-0 tw:bg-transparent tw:p-0"
                    aria-label="View detail"
                  >
                    <span className="tw:!flex tw:h-[30px] tw:w-[30px] tw:!items-center tw:!justify-center tw:rounded-[2px] tw:bg-[#D4B055]">
                      <svg width="18" height="12" viewBox="0 0 18 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path
                          d="M9 0.75C4.795 0.75 1.547 3.459 0.125 5.999C1.547 8.54 4.795 11.249 9 11.249C13.205 11.249 16.453 8.54 17.875 5.999C16.453 3.459 13.205 0.75 9 0.75ZM9 9.75C6.933 9.75 5.25 8.067 5.25 6C5.25 3.933 6.933 2.25 9 2.25C11.067 2.25 12.75 3.933 12.75 6C12.75 8.067 11.067 9.75 9 9.75ZM9 3.75C7.758 3.75 6.75 4.758 6.75 6C6.75 7.242 7.758 8.25 9 8.25C10.242 8.25 11.25 7.242 11.25 6C11.25 4.758 10.242 3.75 9 3.75Z"
                          fill="white"
                        />
                      </svg>
                    </span>
                  </Link>
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  </section>
);

export default InvoicesTable;
